import axios from 'axios'
import _ from 'lodash'
import { useRouter } from 'next/router'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react'

import FullPageSkeleton from '../components/FullPageSkeleton'
import { getPlanWorkspace } from '../lib/helpers'
import { sendToLogDNA } from '../lib/helpers'
import { purgeCache } from '../utils/common'
import { useViewCountAndLimit } from '../utils/hooks/viewCountAndLimit'
import { useAuth } from './AuthProvider'
import { useFirebase } from './FirebaseProvider'
import { usePlatform } from './PlatformProvider'

const WorkspaceContext = createContext()

let oldWorkspace = null,
  oldPlan = null

const WorkspaceProvider = ({ children }) => {
  const { appReady } = usePlatform()
  const { userData, currentPlan, getIdToken } = useAuth()
  const { firebase, Workspace, Display } = useFirebase()
  const [loading, setLoading] = useState(true)
  const [displays, setDisplays] = useState([])
  const { calculateLimit } = useViewCountAndLimit()

  const [workspaces, setWorkspaces] = useState([])
  const [currentWorkspace, _setCurrentWorkspace] = useState(null)

  const workspaceLimit = getPlanWorkspace(currentPlan)
  const { pathname } = useRouter()
  const hideSkelenton = () => {
    return pathname.match(/\/displays\/+/)
  }

  const hasPermissionTo = (subject) => {
    return subject.workspace === currentWorkspace.id
  }
  const setCurrentWorkspace = (workspace) => {
    oldWorkspace = currentWorkspace
    _setCurrentWorkspace(workspace)
    localStorage.setItem(`current-workspace-${userData.userId}`, workspace.id)
  }

  const isWorkspaceOwner = () => {
    return currentWorkspace.userId === userData.userId
  }

  const fetchWorkspaces = async () => {
    let current = null,
      workspaces = await Workspace.getAllInUser(userData.userId),
      invitedWorkspace = null

    //Merge invited workspaces
    if (userData.invitedWorkspaces?.length > 0) {
      const invited = await Workspace.getInvited(
        userData.invitedWorkspaces,
        userData.userId
      )

      if (invited.length > 0) {
        const result = _.groupBy(userData.invitedWorkspaces, 'ownerId')

        const token = await getIdToken()
        for (let ownerId of Object.keys(result)) {
          const res = await axios.post('/api/workspace/ownerInfo', {
            ownerId: ownerId,
            token: token
          })
          invited
            .filter((ws) => ws.userId === ownerId)
            .forEach((ws) => {
              ws.onwnerInfo = res.data
            })
        }

        workspaces = [...workspaces, ...invited]
        invitedWorkspace = invited[0]
      }
    }

    if (workspaces.length > 0) {
      const currentWorkspaceId = localStorage.getItem(
        `current-workspace-${userData.userId}`
      )
      if (currentWorkspaceId) {
        current =
          workspaces.find((workspace) => workspace.id === currentWorkspaceId) ||
          workspaces[0]
      } else if (invitedWorkspace) {
        current = invitedWorkspace
      } else {
        current = workspaces[0]
      }
    } else {
      sendToLogDNA(`Failed to get workspaces: ${userData.userId}`)
      window.alert(
        'Something went wrong, please contact support. hello@promolayer.io'
      )
    }
    if (current) {
      firebase.setWorkspace(current)
      setWorkspaces(workspaces)
      setCurrentWorkspace(current)
    }
  }

  useEffect(() => {
    if (appReady) fetchWorkspaces()
  }, [appReady])

  //Get usage limit at first load and every time the plan changes
  useEffect(() => {
    if (!currentPlan || !currentWorkspace) return

    calculateLimit(currentWorkspace.userId).then(() => {
      //Purge cache everytime plan changes
      if (oldPlan && oldPlan !== currentPlan)
        purgeCache(currentWorkspace.userId, false, true)
    })

    oldPlan = currentPlan
  }, [currentPlan, currentWorkspace])

  useEffect(() => {
    if (!currentWorkspace || !workspaces.length) return
    if (pathname === '/displays') {
      setLoading(true)
      Display.getAllInWorkspace(currentWorkspace)
        .then((displays) => {
          setLoading(false)
          setDisplays(displays)
        })
        .catch((e) => {
          console.error('Failed to fetch displays:', e)
          sendToLogDNA(`Failed to fetch displays: ${e.message}`)
          setLoading(false)
        })
    } else {
      setLoading(false)
    }
  }, [currentWorkspace, workspaces, pathname])

  return loading ? (
    <FullPageSkeleton hide={hideSkelenton()} />
  ) : (
    <WorkspaceContext.Provider
      value={{
        displays,
        setDisplays,
        currentWorkspace,
        currentWorkspacePlan: currentWorkspace.invited
          ? currentWorkspace.onwnerInfo?.currentPlan
          : currentPlan,
        setCurrentWorkspace,

        workspaces,
        setWorkspaces,
        hasPermissionTo,
        isWorkspaceOwner,
        workspaceLimit
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  )
}

const useWorkspace = () => useContext(WorkspaceContext)
export { WorkspaceProvider, useWorkspace }
